import {
  Arrange,
  Container,
  Dropdown,
  FormField,
  Icon,
  Spacer,
  SvgChevronSmallDown,
  Switch,
  Text,
  Tooltip,
} from '@loomhq/lens';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';

import {
  ORG_ROLE_CREATOR,
  ORG_ROLE_CREATOR_LITE,
  ORG_ROLE_DESCRIPTIONS,
  ORG_ROLE_DISPLAY_NAMES,
} from 'constants/organizationRoles';

import styles from './styles.module.less';

const ORG_ROLE_DISPLAY_NAME_OVERRIDE = {
  [ORG_ROLE_CREATOR_LITE]: 'Creator Lite roles',
};

function RoleOption({ displayName, description }) {
  return (
    <div>
      <Text fontWeight="bold">{displayName}</Text>
      <Text color="bodyDimmed">{description}</Text>
    </div>
  );
}

export default function AutoJoinSection({
  domain,
  onChangeAutoJoinEnabled,
  onChangeDefaultJoinRole,
  condensed,
}) {
  const autoJoinRoles = [ORG_ROLE_CREATOR_LITE, ORG_ROLE_CREATOR];
  const [defaultJoinRole, setDefaultJoinRole] = useState(ORG_ROLE_CREATOR_LITE);
  const [autoJoinEnabled, setAutoJoinEnabled] = useState(true);

  useEffect(() => {
    onChangeDefaultJoinRole(defaultJoinRole);
  }, [defaultJoinRole, onChangeDefaultJoinRole]);

  useEffect(() => {
    onChangeAutoJoinEnabled(autoJoinEnabled);
  }, [autoJoinEnabled, onChangeAutoJoinEnabled]);

  const autoJoinOptions = autoJoinRoles.map(role => ({
    key: role,
    title: (
      <RoleOption
        displayName={ORG_ROLE_DISPLAY_NAMES[role]}
        description={ORG_ROLE_DESCRIPTIONS[role]}
      />
    ),
    onClick: () => setDefaultJoinRole(role),
  }));

  function renderCondensed() {
    return (
      <>
        <Switch
          isActive={autoJoinEnabled}
          onChange={() => setAutoJoinEnabled(!autoJoinEnabled)}
        />
        <Spacer bottom="medium" />
        <div className="flex items:center">
          <Text>
            Teammates can automatically join as{' '}
            <Tooltip content="Change default role" placement="top">
              <Dropdown
                className={styles.dropdownCursor}
                trigger={
                  <Arrange tabIndex="0">
                    <Text fontWeight="bold">
                      {ORG_ROLE_DISPLAY_NAME_OVERRIDE[defaultJoinRole] ||
                        pluralize(ORG_ROLE_DISPLAY_NAMES[defaultJoinRole])}
                    </Text>
                    <Icon icon={<SvgChevronSmallDown />} />
                  </Arrange>
                }
                options={autoJoinOptions}
              />
            </Tooltip>
          </Text>
        </div>
        <Spacer bottom="small" />
      </>
    );
  }

  return (
    <Container padding="large" backgroundColor="grey1" radius="medium">
      <FormField>
        {condensed ? (
          renderCondensed()
        ) : (
          <div className="flex items:center">
            <Switch
              isActive={autoJoinEnabled}
              onChange={() => setAutoJoinEnabled(!autoJoinEnabled)}
            />
            <span className="ml:small mr:xsmall">
              <Text> Teammates can automatically join as</Text>
            </span>
            <Tooltip content="Change default role" placement="top">
              <Dropdown
                className={styles.dropdownCursor}
                trigger={
                  <Arrange tabIndex="0">
                    <Text fontWeight="bold">
                      {ORG_ROLE_DISPLAY_NAME_OVERRIDE[defaultJoinRole] ||
                        pluralize(ORG_ROLE_DISPLAY_NAMES[defaultJoinRole])}
                    </Text>
                    <Icon icon={<SvgChevronSmallDown />} />
                  </Arrange>
                }
                options={autoJoinOptions}
              />
            </Tooltip>
          </div>
        )}
      </FormField>
      <Spacer top="small">
        <Text color="grey7" size="small">
          {autoJoinEnabled
            ? `Your teammates can join this Workspace without waiting for permission if they belong to ${domain}.`
            : 'You’ll have to approve each request before your teammates can join this Workspace.'}
        </Text>
      </Spacer>
    </Container>
  );
}
